import { useState, useEffect, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { Approach, ApproachVersion } from 'types/approach';
import { ArcTemplate } from 'types/arc';
import { MarketplaceMetrics } from 'types/marketplace';
import {
  getApproach,
  getArcTemplate,
  GetApproachResponse,
} from '../api/marketplaceApi';

export interface ApproachWithVersion extends Approach {
  version?: ApproachVersion;
  metrics?: MarketplaceMetrics;
}

export interface UseApproachDetailResult {
  approach: ApproachWithVersion | null;
  version: ApproachVersion | null;
  arcTemplate: ArcTemplate | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApproachDetail(
  systemName: string | null,
  selectedVersion?: number
): UseApproachDetailResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [approach, setApproach] = useState<ApproachWithVersion | null>(null);
  const [version, setVersion] = useState<ApproachVersion | null>(null);
  const [arcTemplate, setArcTemplate] = useState<ArcTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId || !systemName) {
      setApproach(null);
      setVersion(null);
      setArcTemplate(null);
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response: GetApproachResponse = await getApproach(
          apiClients.schemaClient,
          systemName,
          {
            version: selectedVersion,
            includeDraft: true,
            includeMetrics: true,
          }
        );

        if (cancelled) return;

        const approachData = response.approach;
        setApproach(approachData);
        const resolvedVersion = approachData.version ?? approachData.latestVersion ?? null;
        setVersion(resolvedVersion);

        // Resolve arc template if the version definition references one
        const arcSystemName =
          resolvedVersion?.definition?.arc?.templateSystemName;
        if (arcSystemName) {
          try {
            const template = await getArcTemplate(
              apiClients.schemaClient,
              arcSystemName
            );
            if (!cancelled) {
              setArcTemplate(template);
            }
          } catch {
            // Arc template fetch is non-critical; leave as null
            if (!cancelled) {
              setArcTemplate(null);
            }
          }
        } else {
          setArcTemplate(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load approach details';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, systemName, selectedVersion, fetchTrigger]);

  return { approach, version, arcTemplate, loading, error, refetch };
}
